import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Save, Shield, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PanelTableCard, PanelTableHeader, PanelTableFrame,
} from "@/components/ui/panel-table";
import {
  PANEL_MENU_PERMISSION_GROUPS,
  applyPanelMenuPermissionToggle,
  finalizePanelMenuPermissions,
} from "@/utils/panelMenuPermissions";
import {
  CORE_PERMISSION_FIELDS,
  PANEL_ROLE_GOREV_OPTIONS,
  emptyPanelRoleForm,
  roleToFormState,
  slugifyRoleName,
} from "@/utils/panelRoles";

const PanelRolesPage = ({ apiBase, isDark, textClass, labelClass, isSuperAdmin = false }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyPanelRoleForm());
  const [isNew, setIsNew] = useState(false);

  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-white" : "";
  const cardTone = isDark ? "border-slate-800 bg-slate-950/40" : "border-gray-200 bg-gray-50/80";

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/panel-roles`);
      const items = res.data?.items || [];
      setRoles(items);
      if (!selectedId && items.length > 0 && !isNew) {
        setSelectedId(items[0].id);
        setForm(roleToFormState(items[0]));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Roller yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [apiBase, selectedId, isNew]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const selectedRole = useMemo(
    () => roles.find((r) => r.id === selectedId) || null,
    [roles, selectedId]
  );

  const selectRole = (role) => {
    setIsNew(false);
    setSelectedId(role.id);
    setForm(roleToFormState(role));
  };

  const startNewRole = () => {
    setIsNew(true);
    setSelectedId(null);
    setForm(emptyPanelRoleForm());
  };

  const handleSave = async () => {
    if (!form.ad.trim()) {
      toast.error("Rol adı zorunlu");
      return;
    }
    const slug = form.slug.trim() || slugifyRoleName(form.ad);
    if (!slug) {
      toast.error("Geçerli bir slug girin");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ad: form.ad.trim(),
        slug,
        description: form.description.trim() || null,
        gorev: form.gorev || null,
        sort_order: Number(form.sort_order) || 100,
        core_permissions: form.core_permissions,
        panel_menu_permissions: finalizePanelMenuPermissions(form.panel_menu_permissions),
      };
      if (isNew) {
        const res = await axios.post(`${apiBase}/panel-roles`, payload);
        toast.success("Rol oluşturuldu");
        setIsNew(false);
        setSelectedId(res.data.id);
        setForm(roleToFormState(res.data));
      } else {
        const res = await axios.put(`${apiBase}/panel-roles?id=${selectedId}`, payload);
        toast.success("Rol güncellendi");
        setForm(roleToFormState(res.data));
      }
      await loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole || selectedRole.is_system) return;
    if (!window.confirm(`"${selectedRole.ad}" rolü silinsin mi?`)) return;
    try {
      await axios.delete(`${apiBase}/panel-roles?id=${selectedRole.id}`);
      toast.success("Rol silindi");
      setSelectedId(null);
      setIsNew(false);
      setForm(emptyPanelRoleForm());
      await loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const toggleMenu = (key, checked) => {
    setForm((prev) => ({
      ...prev,
      panel_menu_permissions: applyPanelMenuPermissionToggle(prev.panel_menu_permissions, key, checked),
    }));
  };

  const toggleCore = (key, checked) => {
    setForm((prev) => ({
      ...prev,
      core_permissions: { ...prev.core_permissions, [key]: checked ? 1 : 0 },
    }));
  };

  return (
    <PanelTableCard isDark={isDark}>
      <PanelTableHeader
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        accent="indigo"
        icon={Shield}
        title="Yetkilendirme Şablonları"
        description="Call center yönetici, datacı, muhasebe gibi rol şablonlarını tanımlayın; kullanıcılara atayın"
        toolbar={(
          <Button onClick={startNewRole} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Yeni Rol
          </Button>
        )}
      />

      <PanelTableFrame isDark={isDark} noPadding>
        {loading ? (
          <div className={`py-20 text-center ${labelClass}`}><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] min-h-[520px]">
            <aside className={`border-b lg:border-b-0 lg:border-r p-3 space-y-1 ${isDark ? "border-slate-800" : "border-gray-200"}`}>
              {roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => selectRole(role)}
                  className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                    selectedId === role.id && !isNew
                      ? (isDark ? "bg-indigo-500/20 text-indigo-200 ring-1 ring-indigo-500/30" : "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200")
                      : (isDark ? "hover:bg-slate-800/80 text-slate-300" : "hover:bg-gray-100 text-gray-700")
                  }`}
                >
                  <div className="font-medium text-sm">{role.ad}</div>
                  <div className={`text-[11px] mt-0.5 ${labelClass}`}>
                    {role.gorev || "görev yok"}
                    {role.is_system ? " · sistem" : ""}
                  </div>
                </button>
              ))}
              {isNew && (
                <div className={`rounded-lg px-3 py-2.5 text-sm font-medium ${isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-50 text-violet-800"}`}>
                  Yeni rol
                </div>
              )}
            </aside>

            <div className="p-4 sm:p-6 space-y-5 overflow-y-auto max-h-[70vh]">
              {(selectedRole || isNew) ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className={`text-base font-semibold ${textClass}`}>
                        {isNew ? "Yeni Rol" : selectedRole.ad}
                      </h3>
                      {!isNew && selectedRole.is_system && (
                        <Badge variant="outline" className="mt-1">Sistem rolü{isSuperAdmin ? "" : " — düzenleme kısıtlı"}</Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!isNew && !selectedRole?.is_system && (
                        <Button type="button" variant="outline" onClick={handleDelete} className="text-rose-600">
                          <Trash2 className="w-4 h-4 mr-1" /> Sil
                        </Button>
                      )}
                      <Button type="button" onClick={handleSave} disabled={saving || (!isNew && selectedRole?.is_system && !isSuperAdmin)}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        Kaydet
                      </Button>
                    </div>
                  </div>

                  <div className={`rounded-xl border p-4 space-y-4 ${cardTone}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Rol Adı</Label>
                        <Input
                          value={form.ad}
                          onChange={(e) => setForm((p) => ({ ...p, ad: e.target.value, slug: p.slug || slugifyRoleName(e.target.value) }))}
                          className={`mt-1 ${inputClass}`}
                        />
                      </div>
                      <div>
                        <Label>Slug</Label>
                        <Input
                          value={form.slug}
                          onChange={(e) => setForm((p) => ({ ...p, slug: slugifyRoleName(e.target.value) }))}
                          className={`mt-1 font-mono text-sm ${inputClass}`}
                          disabled={!isNew && selectedRole?.is_system}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Açıklama</Label>
                      <Textarea
                        rows={2}
                        value={form.description}
                        onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                        className={`mt-1 ${inputClass}`}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Varsayılan Görev (kapsam)</Label>
                        <Select value={form.gorev || "none"} onValueChange={(v) => setForm((p) => ({ ...p, gorev: v === "none" ? "" : v }))}>
                          <SelectTrigger className={`mt-1 ${inputClass}`}><SelectValue /></SelectTrigger>
                          <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                            {PANEL_ROLE_GOREV_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value || "none"} value={opt.value || "none"}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className={`text-xs mt-1 ${labelClass}`}>
                          Call Center Yönetici: takım lideri seçimi kullanıcı atamasında yapılır.
                        </p>
                      </div>
                      <div>
                        <Label>Sıra</Label>
                        <Input
                          type="number"
                          value={form.sort_order}
                          onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                          className={`mt-1 ${inputClass}`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={`rounded-xl border p-4 space-y-3 ${cardTone}`}>
                    <p className={`text-sm font-semibold ${textClass}`}>Ana Yetkiler</p>
                    <p className={`text-xs ${labelClass}`}>Ses dinleme, GSM arama, Excel vb. — sayfa erişiminden ayrı</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {CORE_PERMISSION_FIELDS.map(({ key, label }) => (
                        <label key={key} className={`flex items-center gap-2 rounded-lg px-2 py-2 cursor-pointer ${isDark ? "hover:bg-slate-800/60" : "hover:bg-white"}`}>
                          <Checkbox
                            checked={form.core_permissions[key] === 1}
                            onCheckedChange={(c) => toggleCore(key, c === true)}
                          />
                          <span className={`text-sm ${textClass}`}>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className={`rounded-xl border p-4 space-y-4 ${cardTone}`}>
                    <p className={`text-sm font-semibold ${textClass}`}>Sayfa Erişimleri</p>
                    {PANEL_MENU_PERMISSION_GROUPS.map((group) => (
                      <div key={group.id}>
                        <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${labelClass}`}>{group.label}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {group.items.map(({ key, label, note }) => (
                            <label key={key} className={`flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer ${isDark ? "hover:bg-slate-800/60" : "hover:bg-white"}`}>
                              <Checkbox
                                className="mt-0.5"
                                checked={form.panel_menu_permissions[key] === 1}
                                onCheckedChange={(c) => toggleMenu(key, c === true)}
                              />
                              <span className="min-w-0">
                                <span className={`block text-sm ${textClass}`}>{label}</span>
                                {note && <span className={`block text-[11px] ${labelClass}`}>{note}</span>}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className={`py-16 text-center ${labelClass}`}>Soldan bir rol seçin veya yeni rol oluşturun</div>
              )}
            </div>
          </div>
        )}
      </PanelTableFrame>
    </PanelTableCard>
  );
};

export default PanelRolesPage;
